import { hasRight } from "@/components/crm/crmShared";
import StickyTop from "@/components/common/StickyTop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AviAutoCloseSection from "@/components/settings/AviAutoCloseSection";
import { useViewScope } from "@/context/ViewScopeContext";
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate, guardTab } from "@/hooks/useDeskNavigate";
import {
  Plus, MapPin, X, HelpCircle, LayoutList, Kanban, Clock,
  Info, ChevronDown, ChevronRight, Calendar,
  LayoutGrid, PieChart, CheckCircle2, XCircle, History, Sparkles,
  Layers, Mail, MailOpen, Bell,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { notifyMessagesRead } from "@/lib/unreadMessages";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import AviModuleLogo from "@/components/icons/AviModuleLogo";
import AviCreateDialog from "@/components/avi/AviCreateDialog";
import AviDetailModal from "@/components/avi/AviDetailModal";
import EmptyState from "@/components/ui/EmptyState";
import AviTrashModal from "@/components/avi/AviTrashModal";
import AviOnboardingHint, { shouldShowAviHint } from "@/components/avi/AviOnboardingHint";
import AviKanbanView from "@/components/avi/AviKanbanView";
import AviQuickEditModal from "@/components/avi/AviQuickEditModal";
import AviStatusBadge from "@/components/avi/AviStatusBadge";
// Spec iter139 pt.2 (Feb 2026) — modale partagée de suppression avec motif obligatoire.
import DeleteReasonModal from "@/components/shared/DeleteReasonModal";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import useAviStream from "@/hooks/useAviStream";
import KpiSelectedBadge from "@/components/ui/KpiSelectedBadge";
import useFiltersSnapshot from "@/hooks/useFiltersSnapshot";
import useScrollRestore from "@/hooks/useScrollRestore";
import { MODULE_COLORS } from "@/lib/moduleColors";
import { subtypePalette } from "@/lib/aviSubtypePalette";
// Spec iter173 (2026-02-27) — refactor Avi.jsx : ligne du tableau
// extraite dans son propre composant pour respecter la règle des
// ~700 lignes par fichier.
import AviTableRow from "@/components/avi/AviTableRow";
// Spec iter174 (2026-02-27) — header + filtres extraits dans leurs
// propres composants pour respecter la règle des ~700 lignes.
import AviHeader from "@/components/avi/AviHeader";
import AviFilters from "@/components/avi/AviFilters";

/**
 * Avi — Itération 1 (rev-avi-1, 2026-02-07).
 * Sélecteur de sous-type (Validation par défaut / dernier consulté),
 * KPI, barre de recherche, vue Liste. Création de demandes Validation.
 * Approuver/Refuser/Annuler → itération 2.
 */
const STORAGE_KEY = "avi-last-subtype";

// Spec iter140 pt.3 (Feb 2026) — refonte du filtre Période :
// suppression des presets (7 j / 30 j / 90 j / année) au profit d'une
// sélection PERSONNALISÉE via deux champs date « Du... » / « Au... ».
// La seule option restante du dropdown est « Toutes périodes » (efface
// les 2 dates), le reste passe par les inputs date.
const PERIOD_OPTIONS = [
  { key: "",       label: "Toutes périodes" },
];

function periodToRange(key) {
  // Legacy — plus utilisé après iter140 pt.3 (custom range only). Gardé
  // pour rétro-compat au cas où un ancien preset persiste dans le
  // localStorage. Retour vide pour tout autre valeur.
  return {};
}



export default function Avi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Spec iter142 Phase 2 (Feb 2026) — la fiche AVI s'ouvre désormais
  // en modale overlay au lieu d'une page dédiée. L'URL `/avi/:id` est
  // conservée pour deep-linking / notifications : on lit le paramètre
  // ici et on affiche la modale par-dessus la liste.
  const { id: detailId } = useParams();

  // Spec rev177 (2026-02) — persistance des filtres AVI via le hook
  // partagé `useFiltersSnapshot` (auto-save + TTL 5 min + read au mount).
  // Le composant appelle le hook APRÈS avoir déclaré ses states, mais
  // on a besoin de `initialSnap` AVANT — on utilise donc un placeholder
  // lu directement ici puis on branche l'auto-save via `useFiltersSnapshot`.
  const initialSnap = (() => {
    try {
      const raw = sessionStorage.getItem("avi:filters:v1");
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s?.saved_at || Date.now() - s.saved_at > 5 * 60 * 1000) return null;
      return s;
    } catch { return null; }
  })();

  // Tableau de bord « Action par personne » → `/avi?subtype=accuse&scope=waiting_me` (consommé une fois).
  const urlParams = new URLSearchParams(window.location.search);
  const urlSubtype = ["validation", "accuse", "information"].includes(urlParams.get("subtype")) ? urlParams.get("subtype") : null;
  const [subtype, setSubtype] = useState(() => {
    if (urlSubtype) return urlSubtype;
    // Spec §3 — dernier type consulté (localStorage) + fallback Validation.
    try { return localStorage.getItem(STORAGE_KEY) || "validation"; }
    catch { return "validation"; }
  });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, waiting: 0, approved: 0, refused: 0 });
  const [q, setQ] = useState(initialSnap?.q ?? "");
  // Spec rev163 (2026-02) — debounce 250 ms sur la recherche AVI +
  // liseré animé, cohérent avec Documents.
  const debouncedQ = useDebouncedValue(q, 250);
  const [page, setPage] = useState(initialSnap?.page ?? 1);
  const [sortActivity, setSortActivity] = useState(initialSnap?.sortActivity ?? false);  // spec iter303
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(() => shouldShowAviHint());
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("avi-view") || "list"; } catch { return "list"; }
  });
  // Spec iter102b (2026-08) — SEULE la sélection KPI (scope + statusFilter)
  // est restaurée depuis le snapshot. La recherche, priorité, demandeur,
  // période, toggles clôturés/récents/groupes → retour aux défauts au
  // retour depuis une fiche.
  useEffect(() => { if (urlSubtype) navigate("/avi", { replace: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [scope, setScope] = useState(urlSubtype && urlParams.get("scope") === "waiting_me" ? "waiting_me" : (initialSnap?.scope ?? ""));  // "" | "waiting_me" | "mine" | "witness" | "high_priority"
  const [statusFilter, setStatusFilter] = useState(initialSnap?.statusFilter ?? "");
  // Spec iter198 (2026-02-28) — filtre « Rappel < 24h ». Activé par un
  // clic sur la cloche 🔔 de la carte KPI « En attente ». Doit être
  // combiné avec un scope (mine ou waiting_me) pour cibler POUR/PAR moi.
  const [reminderSoon, setReminderSoon] = useState(initialSnap?.reminderSoon ?? false);
  const [priorityFilter, setPriorityFilter] = useState(initialSnap?.priorityFilter ?? "");
  const [demandeurFilter, setDemandeurFilter] = useState(initialSnap?.demandeurFilter ?? "");
  // Spec iter253 (2026-03-01) — filtre Siège/Projet, aligné sur Documents.
  const [projectFilter, setProjectFilter] = useState(initialSnap?.projectFilter ?? "");
  const [projectsList, setProjectsList] = useState([]);
  const projectsById = useMemo(() => {
    const m = {}; projectsList.forEach((p) => { m[p.id] = p; }); return m;
  }, [projectsList]);
  // Spec iter256 (2026-03-01) — config auto-close AVI, chargée une fois
  // au mount ; sert au rendu de la colonne « Clôturing » (compte à
  // rebours) et à savoir si la feature est active pour ce sous-type.
  const [autoCloseCfg, setAutoCloseCfg] = useState({
    information_hours: null, accuse_hours: null, validation_hours: null,
  });
  const [periodFilter, setPeriodFilter] = useState(initialSnap?.periodFilter ?? "");
  // Spec iter140 pt.3 (Feb 2026) — dates personnalisées « Du » / « Au ».
  // Envoyées telles quelles au backend (query params date_from / date_to).
  const [dateFrom, setDateFrom] = useState(initialSnap?.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(initialSnap?.dateTo ?? "");
  // Spec rev167 (2026-02) — harmonisation AVI ← Documents.
  //  • `showClosed` : masque par défaut les demandes clôturées
  //    (approuvée + refusée). La case à cocher permet de les révéler.
  //  • `showRecent`  : ré-ordonne la liste par activité récente
  //    (dernière modif / message) au lieu de date de création.
  const [showClosed, setShowClosed] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  // Spec rev175 (2026-02) — Grouper par statut (Lot B). Toggle
  // indépendant de « Afficher les clôturés » : sections auto-masquées
  // si count = 0.
  const [groupByStatus, setGroupByStatus] = useState(false);
  // Spec rev157 (2026-02-07) — Grouper par priorité (Lot B point 6).
  // Toggle exclusif avec groupByStatus (l'un désactive l'autre pour
  // éviter deux sectionnements simultanés). Sections vides masquées.
  const [groupByPriority, setGroupByPriority] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  // Spec rev159 (2026-02-07) — Vue globale AVI (admin only). Toggle
  // "mine" ↔ "global" ; défaut "mine" pour tous, admin peut basculer.
  const { viewScope, setViewScope } = useViewScope();
  // Spec iter146 point 3 (Feb 2026) — filtre rapide « Ma to-do AVI ».
  // Quand actif : scope=todo_me_all → agrège les 3 sous-types (Validation
  // à valider + Accusé à recevoir + Information à lire) où l'utilisateur
  // connecté est en attente d'une action. L'onglet sous-type est ignoré
  // côté backend, mais on le grise côté UI pour lever l'ambiguïté.
  const [todoMode, setTodoMode] = useState(false);
  // État Corbeille (modale).
  const [trashOpen, setTrashOpen] = useState(false);
  // Spec iter139 pt.2 (Feb 2026) — modale de suppression AVI.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Spec iter102b — auto-save UNIQUEMENT scope + statusFilter (sélection KPI).
  // iter130 — état de liste complet conservé pour la session (recherche, filtres, tri, page).
  useFiltersSnapshot("avi:filters:v1", {
    scope, statusFilter, q, page, sortActivity, reminderSoon, priorityFilter, demandeurFilter, projectFilter, periodFilter, dateFrom, dateTo,
  });
  useScrollRestore("avi:filters:v1", !loading && items.length > 0);

  const [usersList, setUsersList] = useState([]);
  const usersById = useMemo(() => {
    const m = {}; usersList.forEach((u) => { m[u.id] = u; }); return m;
  }, [usersList]);

  // Spec rev167 (2026-02) — dérivation des items visibles à partir du
  // fetch backend : masque approuvée+refusée sauf si « Afficher les
  // clôturés » est coché, puis re-trie par activité récente si
  // « Activité récente » est actif.
  //
  // Spec iter138 pt.1 (Feb 2026, bug fix) — le masquage
  // approuvée/refusée est SUPPRIMÉ quand l'utilisateur cible
  // explicitement ces statuts, sinon le compteur KPI (ex. « Refusées
  // par moi = 1 ») ne correspond pas au tableau qui filtrait ensuite
  // en local. Cas déclencheurs :
  //   • statusFilter ∈ {"approuvee", "refusee"} (chip statut manuel)
  //   • scope ∈ {"received_approved", "received_refused"} (KPI clic)
  // Spec iter144/iter145 (Feb 2026) — polymorphie par sous-type.
  const isAccuse = subtype === "accuse";
  const isInfo = subtype === "information";
  const targetsClosedStatuses =
    statusFilter === "approuvee" ||
    statusFilter === "accusee" ||
    statusFilter === "lue" ||
    statusFilter === "refusee" ||
    scope === "received_approved" ||
    scope === "received_refused";
  const visibleItems = useMemo(() => {
    let arr = items;
    if (!showClosed && !targetsClosedStatuses) {
      arr = arr.filter((it) =>
        it.status !== "approuvee"
        && it.status !== "accusee"
        && it.status !== "lue"
        && it.status !== "refusee"
      );
    }
    if (showRecent) {
      arr = [...arr].sort((a, b) => {
        const ta = new Date(a.last_activity_at || a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.last_activity_at || b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
    }
    return arr;
  }, [items, showClosed, showRecent, targetsClosedStatuses]);

  const meta = AVI_SUBTYPES[subtype];

  // Spec iter155 (2026-02-25) — remonté au-dessus de ``load`` qui l'utilise,
  // pour satisfaire ``no-use-before-define`` (garde-fou TDZ).
  const [configOpen, setConfigOpen] = useState(false);
  const canAdmin = hasRight(user, "avi.delete"); // iter200 — droit matriciel, plus de rôle
  const aviScope = user?.is_first_admin ? "all" : (user?.rights?.avi?.scope || "own");

  useEffect(() => {
    // Charge la liste des utilisateurs (pour avatars + dropdown demandeur).
    api.get("/users/directory")
      .then((r) => setUsersList(r.data.items || r.data || []))
      .catch(() => setUsersList([]));
    // Spec iter253 — projets pour la nouvelle colonne + dropdown filtre.
    api.get("/projects")
      .then((r) => setProjectsList(r.data || []))
      .catch(() => setProjectsList([]));
    // Spec iter256 — config auto-close AVI (paramétrable admin).
    api.get("/avi/config/auto-close")
      .then((r) => setAutoCloseCfg(r.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, subtype); } catch {}
  }, [subtype]);
  useEffect(() => {
    try { localStorage.setItem("avi-view", view); } catch {}
  }, [view]);

  // Point 3 (Sept 2026) — garde anti-course : deux chargements successifs
  // (hydratation du scope Perso/Globale, filtre, poll) pouvaient répondre
  // dans le désordre et une réponse périmée (souvent vide) écrasait la liste.
  const loadSeq = useRef(0);
  const loadRef = useRef(null);
  const load = async ({ silent = false } = {}) => {
    const seq = ++loadSeq.current;
    if (!silent) setLoading(true);
    try {
      const per_page = view === "kanban" ? 200 : 50;
      // Spec iter146 point 3 — quand todoMode est actif, on override
      // scope=todo_me_all et on ignore l'avi_type (le backend fait pareil).
      const listParams = todoMode
        ? { q: debouncedQ, page, per_page, scope: "todo_me_all" }
        : { avi_type: subtype, q: debouncedQ, page, per_page };
      if (!todoMode && scope) listParams.scope = scope;
      if (sortActivity) listParams.sort = "recent_activity";
      if (!todoMode && statusFilter) listParams.status = statusFilter;
      // Spec iter198 (2026-02-28) — filtre rappel < 24h.
      if (!todoMode && reminderSoon) listParams.reminder_soon = true;
      if (priorityFilter) listParams.priority = priorityFilter;
      if (demandeurFilter) listParams.demandeur_id = demandeurFilter;
      if (projectFilter) listParams.project_id = projectFilter;
      // Spec rev159 (2026-02-07) — envoi de view_scope si admin+global.
      if (viewScope === "global") listParams.view_scope = "global";
      else if (canAdmin && viewScope.startsWith("user:")) { listParams.view_scope = "mine"; listParams.view_as = viewScope.slice(5); }
      else if (viewScope === "mine") listParams.view_scope = "mine";
      Object.assign(listParams, periodToRange(periodFilter));
      // Spec iter140 pt.3 (Feb 2026) — plage personnalisée envoyée
      // directement au backend. Écrase periodToRange qui est vide.
      if (dateFrom) listParams.date_from = dateFrom;
      if (dateTo) listParams.date_to = dateTo;
      const [listRes, summaryRes] = await Promise.all([
        api.get("/avi", { params: listParams }),
        api.get("/avi/summary", { params: { avi_type: subtype } }),
      ]);
      if (seq !== loadSeq.current) return;  // réponse périmée — ignorée
      setItems(listRes.data.items || []);
      setTotalPages(Math.max(1, Math.ceil((listRes.data.total || 0) / (listRes.data.per_page || 50))));
      setSummary(summaryRes.data);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      toast.error("Erreur de chargement", { description: e?.response?.data?.detail || "" });
    } finally {
      if (!silent && seq === loadSeq.current) setLoading(false);
    }
  };

  // Spec iter156 — dépendance ``load`` volontairement omise (refetch
  // guidé par le tableau de deps métier ci-dessous). ``load`` est
  // recréée à chaque render : l'inclure produirait une boucle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [
    subtype, debouncedQ, page, scope, view, viewScope, todoMode,
    statusFilter, priorityFilter, demandeurFilter, projectFilter, periodFilter, dateFrom, dateTo,
    reminderSoon, sortActivity,
  ]);

  // Rev-avi-3 — SSE : recharger la liste dès qu'un événement AVI arrive.
  // On filtre côté client sur `avi_type` pour éviter les recharges inutiles
  // quand on est sur "Validation" et qu'un accusé/information est modifié.
  // Spec iter191 (2026-02-27) — SSE-triggered load passe en mode `silent`
  // pour ne pas faire clignoter la liste sur les événements distants.
  useAviStream({
    onAny: (evt) => {
      if (!evt || evt.avi_type === subtype || evt.avi_type == null) {
        load({ silent: true });
      }
    },
  });

  // Spec iter191 (2026-02-27) — Point 3 : polling léger 20s (silent
  // refetch quand l'onglet est visible) + refetch au retour de focus.
  // Filet de sécurité en complément de SSE (si la connexion tombe, ce
  // polling ramène quand même les nouveaux AVIs sans reset visuel).
  loadRef.current = load; // toujours la fermeture du rendu courant (sous-type + filtres actuels)
  useEffect(() => {
    const soft = () => loadRef.current?.({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") soft();
    };
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") soft();
    }, 20000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAccuseOrInfo = subtype !== "validation";

  // Rev post-prod-4 point 1 — compteur de conversations avec messages non lus.
  const unreadConversationCount = useMemo(
    () => (items || []).filter((it) => (it.messages_unread || 0) > 0).length,
    [items],
  );

  const markAllRead = async () => {
    try {
      await api.post("/avi/messages/read-all");
      notifyMessagesRead();
      toast.success("Tout marqué comme lu");
      load();
      try { window.dispatchEvent(new Event("avi:msg-pulse")); } catch {}
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };
  const handleDelete = (it, e) => {
    e.stopPropagation();
    // Spec iter139 pt.2 (Feb 2026) — remplace le window.confirm par
    // une vraie modale d'app avec motif OBLIGATOIRE (parité Tâches /
    // Documents). Le motif est enregistré côté backend dans
    // ``deleted_reason`` et inclus dans l'email d'alerte Premier Admin.
    setDeleteTarget(it);
  };
  const confirmDelete = async (reason) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/avi/${deleteTarget.id}`, { data: { reason } });
      const t = deleteTarget;
      toast.deleted("demande", t.reference, { onUndo: async () => { await api.post(`/avi/${t.id}/restore`); load(); } });
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error("Échec de la suppression", { description: err?.response?.data?.detail || "" });
    } finally {
      setDeleteBusy(false);
    }
  };

  // Spec rev175 (2026-02) — ordre fixe des sections « Grouper par statut »
  // (Lot B). Sections vides masquées : quand `showClosed` est OFF, les
  // sections Approuvées/Refusées ont naturellement 0 ligne → auto-hide.
  const GROUP_ORDER = [
    { key: "en_attente",      label: "En attente",   color: "#F9BC26" },
    { key: "partiel_repondu", label: "Partiel",      color: "#F97316" },
    { key: "approuvee",       label: "Approuvées",   color: "#4ADE80" },
    { key: "refusee",         label: "Refusées",     color: "#F87171" },
  ];
  // Spec rev157 (2026-02-07) — ordre fixe des sections « Grouper par
  // priorité » : Immédiat → Urgent → Moyen → Normal. Sections vides
  // masquées comme pour Grouper par statut.
  const GROUP_PRIORITY_ORDER = [
    { key: "immediat", label: "Immédiat", color: "#EF4444" },
    { key: "urgent",   label: "Urgent",   color: "#F97316" },
    { key: "moyen",    label: "Moyen",    color: "#F9BC26" },
    { key: "normal",   label: "Normal",   color: "#4ADE80" },
  ];
  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-3" data-testid="avi-page">
      {/* Onboarding — carte d'aide dismissible (rev-avi-2 bis) — hors zone fixe (carte ponctuelle, 275 px) */}
      {showHint && (
        <AviOnboardingHint onDismiss={() => setShowHint(false)} />
      )}
      <StickyTop gap={12} testId="avi-sticky-top">
      <div className="space-y-3">

      {/* Header — extrait dans AviHeader.jsx (spec iter174). */}
      <AviHeader
        subtype={subtype}
        setSubtype={setSubtype}
        meta={meta}
        viewScope={viewScope}
        setViewScope={setViewScope}
        summary={summary}
        unreadConversationCount={unreadConversationCount}
        onMarkAllRead={markAllRead}
        canAdmin={canAdmin}
        onOpenTrash={() => setTrashOpen(true)}
        canConfig={hasRight(user, "avi.config_autoclose")}
        onOpenConfig={() => setConfigOpen(true)}
      />
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-3xl" data-testid="avi-config-dialog">
          <DialogHeader><DialogTitle>Configuration du module AVI</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto"><AviAutoCloseSection /></div>
        </DialogContent>
      </Dialog>

      {/* Bande 2 déplacée à l'intérieur du wrapper ci-dessus. */}

      {/* ==============================================================
          Ligne 2 — Bandeau KPI (spec iter196 pt.2, 2026-02-28).
          Refonte : 2 groupes clairement séparés avec chacun leur
          label au-dessus (« POUR MOI » / « PAR MOI ») en petit
          majuscule couleur module ; fine ligne verticale grise
          entre les deux ; première carte de chaque groupe (Envoyées /
          Reçues) avec fond plein couleur module + texte blanc ; les
          cartes suivantes (En attente / Approuvées / Refusées) fond
          sombre uni, libellé en texte coloré selon le statut (jaune,
          vert, rouge).
          ============================================================== */}
      <div
        className="flex flex-row gap-2"
        data-testid="avi-kpi"
      >
        <div className="flex-1 min-w-0">
        {(() => {
          // Palette polymorphe par sous-type.
          const palette = subtypePalette(subtype);
          const positiveByLabel = isInfo ? "Lues par moi" : isAccuse ? "Reçues par moi" : "Approuvées par moi";
          const positiveForLabel = isInfo ? "Lues pour moi" : isAccuse ? "Reçues pour moi" : "Approuvées pour moi";
          const positiveStatus = isInfo ? "lue" : isAccuse ? "accusee" : "approuvee";
          // Palette sémantique des statuts (indépendante du module).
          const COLOR_WAITING = "#F5C842";  // jaune — en attente
          const COLOR_POSITIVE = "#22C55E"; // vert — approuvé / lu / accusé
          const COLOR_REFUSED = "#EF4444";  // rouge — refusé
          const showRefused = !isInfo && !isAccuse;
          // Cartes "Pour moi" (Envoyées) — la 1ère est le total (fond plein).
          const sentCards = [
            { key: "sent_total",    label: "Envoyées",  value: summary.sent_total    ?? 0, scope: "mine",   status: "",             kind: "total" },
            { key: "sent_waiting",  label: isInfo ? "En attente" : "En attente", value: summary.sent_waiting  ?? 0, scope: "mine",   status: "en_attente", statusColor: COLOR_WAITING, showBell: (summary.sent_reminder_soon ?? 0) > 0, urgentBell: (summary.sent_reminder_urgent ?? 0) > 0 },
            { key: "sent_approved", label: positiveForLabel.replace(" pour moi", ""), value: summary.sent_approved ?? 0, scope: "mine", status: positiveStatus, statusColor: COLOR_POSITIVE },
            ...(showRefused ? [{ key: "sent_refused", label: "Refusées", value: summary.sent_refused ?? 0, scope: "mine", status: "refusee", statusColor: COLOR_REFUSED }] : []),
          ];
          // Cartes "Par moi" (Reçues).
          const receivedCards = [
            { key: "received_total",       label: "Reçues",  value: summary.received_total       ?? 0, scope: "received",          status: "",           kind: "total" },
            { key: "received_waiting_me",  label: "En attente", value: summary.received_waiting_me  ?? 0, scope: "waiting_me",        status: "", statusColor: COLOR_WAITING, showBell: (summary.received_reminder_soon ?? 0) > 0, urgentBell: (summary.received_reminder_urgent ?? 0) > 0 },
            { key: "received_approved",    label: positiveByLabel.replace(" par moi", ""), value: summary.received_approved    ?? 0, scope: "received_approved", status: "", statusColor: COLOR_POSITIVE },
            ...(showRefused ? [{ key: "received_refused", label: "Refusées", value: summary.received_refused ?? 0, scope: "received_refused", status: "", statusColor: COLOR_REFUSED }] : []),
          ];
          const renderCard = (k) => {
            // Spec iter198 (2026-02-28) — Un clic sur la cloche 🔔
            // active un filtre supplémentaire `reminderSoon` (rappel
            // < 24h) tout en gardant les filtres de scope/status de la
            // carte. Un 2ᵉ clic sur la cloche le retire.
            const bellActive = k.showBell && reminderSoon
              && scope === k.scope
              && (statusFilter || "") === (k.status || "");
            const active = scope === k.scope
              && (statusFilter || "") === (k.status || "")
              && (!reminderSoon || bellActive);
            const isTotal = k.kind === "total";
            let cardStyle;
            if (isTotal) {
              // Total : fond plein couleur module, texte blanc.
              cardStyle = active
                ? {
                    background: meta.color,
                    border: "1px solid #FFFFFF66",
                    boxShadow: `0 0 0 2px ${meta.color}44`,
                  }
                : {
                    background: meta.color,
                    border: "1px solid transparent",
                  };
            } else {
              // Cartes de statut : fond sombre uni, libellé coloré.
              cardStyle = active
                ? {
                    background: "#232326",
                    border: `1px solid ${k.statusColor}88`,
                  }
                : {
                    background: "#18181a",
                    border: "1px solid #232326",
                  };
            }
            const numberColor = isTotal
              ? "#FFFFFF"
              : (k.value > 0 ? k.statusColor : "#6B7280");
            const labelColor = isTotal
              ? "#FFFFFF"
              : k.statusColor;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => {
                  if (active) {
                    setScope(""); setStatusFilter(""); setReminderSoon(false); setPage(1); return;
                  }
                  setScope(k.scope || "");
                  setStatusFilter(k.status || "");
                  // Un clic sur la carte (hors cloche) retire le filtre
                  // rappel<24h pour ré-afficher tout le statut.
                  setReminderSoon(false);
                  setPage(1);
                }}
                className="flex flex-col items-center justify-center rounded-md transition-all min-w-0 relative"
                style={{
                  ...cardStyle,
                  padding: "6px 8px",
                  minHeight: 62,
                  cursor: "pointer",
                }}
                data-testid={`avi-kpi-${k.key}`}
                title={k.label}
              >
                {active && <KpiSelectedBadge testId={`avi-kpi-${k.key}-selected`} />}
                <span
                  className="font-bold tabular-nums"
                  style={{ fontSize: 22, lineHeight: 1, color: numberColor, textShadow: isTotal ? "0 1px 2px rgba(0,0,0,0.35)" : "none" }}
                >
                  {k.value}
                </span>
                <span
                  className="uppercase mt-1 truncate max-w-full inline-flex items-center justify-center gap-1"
                  style={{
                    fontSize: 10.5, letterSpacing: "0.05em",
                    color: labelColor,
                    fontWeight: 700,
                  }}
                >
                  {k.showBell && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Toggle : si déjà actif sur cette carte, on retire tout.
                        if (bellActive) {
                          setReminderSoon(false);
                          setScope(""); setStatusFilter("");
                          setPage(1);
                          return;
                        }
                        setScope(k.scope || "");
                        setStatusFilter(k.status || "");
                        setReminderSoon(true);
                        setPage(1);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault(); e.stopPropagation();
                          e.currentTarget.click();
                        }
                      }}
                      className={`inline-flex items-center justify-center rounded-full transition-all cursor-pointer${k.urgentBell && !bellActive ? " avi-bell-pulse" : ""}`}
                      style={{
                        width: 16, height: 16,
                        background: bellActive ? labelColor : "transparent",
                        border: bellActive ? "none" : `1px solid ${labelColor}55`,
                        marginRight: 1,
                      }}
                      aria-label="Filtrer les demandes dont le rappel se déclenche dans les 24h"
                      title="Voir uniquement les demandes dont le rappel se déclenche dans les 24h"
                      data-testid={`avi-kpi-${k.key}-bell`}
                    >
                      <Bell
                        size={9}
                        strokeWidth={2.8}
                        style={{
                          color: bellActive ? "#1B1B1B" : labelColor,
                          flexShrink: 0,
                        }}
                      />
                    </span>
                  )}
                  <span className="truncate">{k.label}</span>
                </span>
              </button>
            );
          };
          return (
            <div
              className="flex items-stretch"
              style={{ background: "#1E1E1E", border: "1px solid #2A2A2A", borderRadius: 12, padding: "10px 12px", gap: 10 }}
              data-testid="avi-kpi-fused-line"
            >
              {/* Spec iter208ter (2026-02-28) — Ordre + libellés corrigés :
                  • POUR MOI (gauche) = ce qui m'est adressé = receivedCards
                  • PAR MOI (droite, adjacent au bouton +) = ce que j'ai
                    initié/envoyé = sentCards
                  Auparavant les groupes étaient inversés (label ↔ contenu). */}
              {/* Groupe « POUR MOI » (reçues) — à gauche --------------- */}
              <div className="flex-1 min-w-0 flex flex-col" data-testid="avi-kpi-group-received">
                <div
                  className="uppercase mb-1.5 text-center"
                  style={{
                    fontSize: 9.5,
                    letterSpacing: "0.14em",
                    fontWeight: 700,
                    color: meta.color,
                  }}
                >
                  Pour moi
                </div>
                <div
                  className="grid items-stretch flex-1"
                  style={{
                    gridTemplateColumns: `repeat(${receivedCards.length}, minmax(0, 1fr))`,
                    gap: 6,
                  }}
                >
                  {receivedCards.map(renderCard)}
                </div>
              </div>
              {/* Séparateur vertical fin */}
              <div
                aria-hidden="true"
                className="self-stretch"
                style={{
                  width: 1,
                  background: "linear-gradient(to bottom, transparent 0%, #3A3A3A 15%, #3A3A3A 85%, transparent 100%)",
                }}
                data-testid="avi-kpi-divider"
              />
              {/* Groupe « PAR MOI » (envoyées) — à droite, adjacent au + */}
              <div className="flex-1 min-w-0 flex flex-col" data-testid="avi-kpi-group-sent">
                <div
                  className="uppercase mb-1.5 text-center"
                  style={{
                    fontSize: 9.5,
                    letterSpacing: "0.14em",
                    fontWeight: 700,
                    color: meta.color,
                  }}
                >
                  Par moi
                </div>
                <div
                  className="grid items-stretch flex-1"
                  style={{
                    gridTemplateColumns: `repeat(${sentCards.length}, minmax(0, 1fr))`,
                    gap: 6,
                  }}
                >
                  {sentCards.map(renderCard)}
                </div>
              </div>
            </div>
          );
        })()}
        </div>

        {/* Bouton « + <type> » fusionné sur les 2 lignes (iter138 pt.2).
            Spec iter163 (Feb 2026) — alignement exact : ``alignSelf:
            stretch`` + ``height:100%`` + ``boxSizing: border-box``
            garantit que le bouton occupe strictement la hauteur des 2
            rangées KPI empilées à gauche, sans débordement au dessus/
            dessous, quelle que soit la densité de contenu. */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!meta.enabled}
          className="flex flex-col items-center justify-center rounded-xl transition-all shrink-0"
          style={{
            width: 80,
            alignSelf: "stretch",
            height: "auto",
            boxSizing: "border-box",
            background: meta.enabled ? meta.color : "#2A2A2A",
            border: meta.enabled ? `1px solid ${meta.color}` : "1px solid #3A3A3A",
            color: "#FFFFFF",
            cursor: meta.enabled ? "pointer" : "not-allowed",
            boxShadow: meta.enabled ? `0 4px 14px ${meta.color}55` : "none",
          }}
          onMouseEnter={(e) => { if (meta.enabled) e.currentTarget.style.filter = "brightness(1.15)"; }}
          onMouseLeave={(e) => { if (meta.enabled) e.currentTarget.style.filter = "brightness(1)"; }}
          data-testid="avi-new-btn"
          title={meta.enabled ? `Nouvelle ${meta.label}` : "Bientôt disponible"}
        >
          <span
            style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: "#FFFFFF" }}
            aria-hidden="true"
          >+</span>
          <span
            className="uppercase mt-2 truncate max-w-full block text-center"
            style={{
              // Spec iter164j (2026-02-25) — Fix débordement « INFORMATION »
              // (11 chars) sur le bouton fixe 80px de largeur. On adapte la
              // taille de police en fonction de la longueur du libellé :
              //   • ≤ 8  chars : 11px (défaut historique — VALIDATION, ACCUSÉ)
              //   • ≥ 9  chars : 9.5px + letter-spacing resserré
              // Le `truncate` reste en filet de sécurité pour un futur
              // libellé encore plus long.
              fontSize: (meta.label || "").length >= 9 ? 9.5 : 11,
              letterSpacing: (meta.label || "").length >= 9 ? "0.04em" : "0.06em",
              fontWeight: 800, color: "#FFFFFF",
              lineHeight: 1.15,
            }}
          >
            {meta.label}
          </span>
        </button>
      </div>

      {/* Filtres — extraits dans AviFilters.jsx (spec iter174). */}
      <AviFilters
        isInfo={isInfo}
        isAccuse={isAccuse}
        q={q}
        setQ={setQ}
        debouncedQ={debouncedQ}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        demandeurFilter={demandeurFilter}
        setDemandeurFilter={setDemandeurFilter}
        projectFilter={projectFilter}
        setProjectFilter={setProjectFilter}
        projectsList={projectsList}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        setPeriodFilter={setPeriodFilter}
        scope={scope}
        setScope={setScope}
        usersList={usersList}
        setPage={setPage}
      />

      {/* ==============================================================
          Ligne au-dessus du tableau — Toggle vue à gauche, options à
          droite. Spec rev176 (2026-02) : inversion pour matcher
          l'agencement de Tâches et Documents (toggle à gauche).
          ============================================================== */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Gauche — Toggle Liste / Kanban */}
        <div className="flex items-center gap-1 rounded-lg"
             style={{ background: "#1A1A1A", border: "1px solid #2A2A2A" }}
             data-testid="avi-view-toggle">
          {[
            { key: "list",   label: "Liste",  Icon: LayoutList },
            { key: "kanban", label: "Kanban", Icon: Kanban },
          ].map((v) => {
            const active = view === v.key;
            const Icon = v.Icon;
            return (
              <button
                key={v.key}
                type="button"
                onClick={guardTab(() => setView(v.key))}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-all"
                style={{
                  background: active ? "#212121" : "transparent",
                  color: active ? "#EDEDED" : "#9CA3AF",
                  border: active ? `1px solid ${meta.color}55` : "1px solid transparent",
                }}
                data-testid={`avi-view-${v.key}`}
              >
                <Icon size={12} /> {v.label}
              </button>
            );
          })}
        </div>
        {/* Droite — Ma to-do + Activité récente + Afficher les clôturés + compteur */}
        <div className="flex items-center gap-2">
          {/* Spec iter146 point 3 (Feb 2026) — filtre rapide « Ma to-do AVI » :
              agrège les 3 sous-types (Validation à valider + Accusé à
              recevoir + Information à lire) dont l'utilisateur connecté
              est en attente d'une action. Toggle exclusif — désactive
              l'onglet sous-type courant.
              Spec iter170 (2026-02-27) — sur l'onglet Information, le
              nouveau modèle (auto-clôture dès lecture par tous) rend le
              bouton inutile : caché entièrement quand Info actif. */}
          {!isInfo && (
            <button
              type="button"
              onClick={() => setTodoMode((v) => !v)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold transition-all"
              style={{
                background: todoMode ? "rgba(249,188,38,0.16)" : "#1A1A1A",
                border: `1px solid ${todoMode ? "#F9BC26" : "#2A2A2A"}`,
                color: todoMode ? "#F9BC26" : "#9CA3AF",
              }}
              title="Regroupe tous les sous-types (Validation + Accusé + Information) en attente de votre réponse"
              data-testid="avi-toggle-todo-me"
            >
              <CheckCircle2 size={13} /> Ma to-do AVI
            </button>
          )}
          {/* Spec iter163 (Feb 2026) — retrait « Activité récente »
              (peu utilisé, redondant avec le tri par défaut). */}
          <label
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-[12px] font-medium cursor-pointer transition-all select-none"
            style={{
              background: showClosed ? "#1A1A1A" : "transparent",
              border: `1px solid ${showClosed ? "#3A3A3A" : "transparent"}`,
              color: showClosed ? "#EDEDED" : "#9CA3AF",
            }}
            data-testid="avi-toggle-closed"
            title='"Clôturé" = Approuvée + Refusée'
          >
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
              className="w-3.5 h-3.5 accent-pink-500"
            />
            Afficher les clôturés
          </label>
          <div className="text-[11px] text-muted-foreground tabular-nums ml-2">
            {visibleItems.length} {visibleItems.length > 1 ? "résultats" : "résultat"}
          </div>
        </div>
      </div>

      </div>
      </StickyTop>

      {/* Vue Kanban ou Liste */}
      {view === "kanban" ? (
        <AviKanbanView
          items={visibleItems}
          loading={loading}
          currentUserId={user?.id}
          subtype={subtype}
          onOpen={(it) => navigate(`/avi/${it.id}`)}
        />
      ) : (
      <div className="rounded-xl" style={{ border: "1px solid #2A2A2A", background: "#161616", overflow: "clip" }}>
        <table className="w-full text-sm" data-testid="avi-list-table">
          <thead data-sticky-head="" style={{ "--desk-head-bg": "#1A1A1A" }}>
            <tr className="text-[10px] uppercase tracking-widest text-muted-foreground" style={{ background: "#1A1A1A" }}>
              <th className="text-left py-2.5 pl-5 pr-3">Réf.</th>
              <th className="text-left py-2.5 px-3">Date et heure</th>
              <th className="text-left py-2.5 px-3">Objet</th>
              <th className="text-left py-2.5 px-3">Demandeur</th>
              {/* Spec iter253 (2026-03-01) — Siège / Projet, alignée avec
                  Documents et Tâches. Position : après Demandeur, avant
                  la colonne polymorphe des destinataires. */}
              <th className="text-left py-2.5 px-3">Siège / Projet</th>
              <th className="text-left py-2.5 px-3">{isAccuse ? "Réceptionnaires" : isInfo ? "Lecteurs" : "Décideurs"}</th>
              {/* Spec iter180 pt.3 (2026-02-27) — colonne « Témoins »
                  retirée sur Information (rôle Témoin supprimé du sous-type
                  Information — spec iter170). Conservée sur Validation et
                  Accusé où le rôle reste actif. */}
              {!isInfo && <th className="text-left py-2.5 px-3">Témoins</th>}
              <th className="text-left py-2.5 px-3">Priorité</th>
              <th className="text-center py-2.5 px-3">Lecture</th>
              {/* Spec iter256 (2026-03-01) — Clôturing : compte à rebours
                  avant clôture auto quand tous les destinataires ont lu.
                  Masquée si la feature est OFF pour ce sous-type. */}
              {autoCloseCfg?.[`${subtype}_hours`] ? (
                <th className="text-center py-2.5 px-3" data-testid="avi-th-cloturing">Clôturing</th>
              ) : null}
              <th className="text-center py-2.5 px-3 cursor-pointer select-none hover:text-white transition-colors"
                onClick={() => { setSortActivity((v) => !v); setPage(1); }}
                title={sortActivity ? "Trié par activité récente — cliquer pour revenir au tri par date" : "Trier par activité récente"}
                style={{ color: sortActivity ? "#0078D4" : undefined }} data-testid="avi-th-activity">
                Activité{sortActivity ? " ↓" : ""}
              </th>
              <th className="text-center py-2.5 px-3">Discussion</th>
              <th className="text-center py-2.5 px-3">Fichiers</th>
              <th className="text-center py-2.5 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleItems.length === 0 && (
              <tr>
                <td colSpan={(isInfo ? 12 : 13) + (autoCloseCfg?.[`${subtype}_hours`] ? 1 : 0)} className="p-0" data-testid="avi-empty">
                  {/* Spec iter130 (Feb 2026) — EmptyState unifié.
                      Spec iter164O (2026-02-27) — remplace l'icône flat
                      par le AviModuleLogo (carré arrondi dégradé) pour
                      cohérence avec le header du module. */}
                  <EmptyState
                    icon={<AviModuleLogo subtype={subtype} size={56} />}
                    title={`Aucune demande de ${meta.label.toLowerCase()}`}
                    description="Créez la première pour démarrer, ou changez de sous-type."
                    ctaLabel="+ Créer une demande"
                    onCta={() => setCreateOpen(true)}
                    color={meta.color}
                    testId="avi-empty-state"
                  />
                </td>
              </tr>
            )}
            {/* Spec rev175 (2026-02) — mode grouper par statut :
                4 sections repliables dans l'ordre En attente → Partiel
                → Approuvées → Refusées. Sections vides masquées. */}
            {groupByStatus ? (
              GROUP_ORDER.flatMap((g) => {
                const rows = visibleItems.filter((x) => x.status === g.key);
                if (rows.length === 0) return [];
                const collapsed = collapsedGroups.has(g.key);
                const Chev = collapsed ? ChevronRight : ChevronDown;
                const header = (
                  <tr
                    key={`avi-group-hdr-${g.key}`}
                    onClick={() => toggleGroup(g.key)}
                    className="cursor-pointer select-none"
                    style={{ background: "#161616", borderTop: "1px solid #2A2A2A" }}
                    data-testid={`avi-group-hdr-${g.key}`}
                  >
                    <td colSpan={(isInfo ? 12 : 13) + (autoCloseCfg?.[`${subtype}_hours`] ? 1 : 0)} className="py-2 pl-4 pr-3">
                      <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider"
                           style={{ color: g.color }}>
                        <Chev size={13} strokeWidth={2.4} />
                        <span>{g.label}</span>
                        <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-full"
                              style={{ background: `${g.color}22`, color: g.color }}>
                          {rows.length}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
                return collapsed ? [header] : [header, ...rows.map((it) => (
                  <AviTableRow
                    key={it.id}
                    it={it}
                    subtype={subtype}
                    meta={meta}
                    isAccuse={isAccuse}
                    isInfo={isInfo}
                    usersById={usersById}
                    projectsById={projectsById}
                    showCloturing={!!autoCloseCfg?.[`${subtype}_hours`]}
                    autoCloseHours={autoCloseCfg?.[`${subtype}_hours`] || null}
                    user={user}
                    canAdmin={canAdmin}
                    aviScope={aviScope}
                    onEdit={setEditTarget}
                    onDelete={handleDelete}
                  />
                ))];
              })
            ) : groupByPriority ? (
              /* Spec rev157 (2026-02-07) — mode grouper par priorité :
                 même mécanique que par statut, 4 sections
                 Immédiat → Urgent → Moyen → Normal. */
              GROUP_PRIORITY_ORDER.flatMap((g) => {
                const rows = visibleItems.filter((x) => x.priority === g.key);
                if (rows.length === 0) return [];
                const collapsed = collapsedGroups.has(`pri:${g.key}`);
                const Chev = collapsed ? ChevronRight : ChevronDown;
                const header = (
                  <tr
                    key={`avi-priority-hdr-${g.key}`}
                    onClick={() => toggleGroup(`pri:${g.key}`)}
                    className="cursor-pointer select-none"
                    style={{ background: "#161616", borderTop: "1px solid #2A2A2A" }}
                    data-testid={`avi-priority-hdr-${g.key}`}
                  >
                    <td colSpan={(isInfo ? 12 : 13) + (autoCloseCfg?.[`${subtype}_hours`] ? 1 : 0)} className="py-2 pl-4 pr-3">
                      <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider"
                           style={{ color: g.color }}>
                        <Chev size={13} strokeWidth={2.4} />
                        <span
                          className="inline-block rounded-full shrink-0"
                          style={{ width: 8, height: 8, background: g.color }}
                          aria-hidden="true"
                        />
                        <span>{g.label}</span>
                        <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-full"
                              style={{ background: `${g.color}22`, color: g.color }}>
                          {rows.length}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
                return collapsed ? [header] : [header, ...rows.map((it) => (
                  <AviTableRow
                    key={it.id}
                    it={it}
                    subtype={subtype}
                    meta={meta}
                    isAccuse={isAccuse}
                    isInfo={isInfo}
                    usersById={usersById}
                    projectsById={projectsById}
                    showCloturing={!!autoCloseCfg?.[`${subtype}_hours`]}
                    autoCloseHours={autoCloseCfg?.[`${subtype}_hours`] || null}
                    user={user}
                    canAdmin={canAdmin}
                    aviScope={aviScope}
                    onEdit={setEditTarget}
                    onDelete={handleDelete}
                  />
                ))];
              })
            ) : (
              visibleItems.map((it) => (
                <AviTableRow
                  key={it.id}
                  it={it}
                  subtype={subtype}
                  meta={meta}
                  isAccuse={isAccuse}
                  isInfo={isInfo}
                  usersById={usersById}
                  projectsById={projectsById}
                  showCloturing={!!autoCloseCfg?.[`${subtype}_hours`]}
                  autoCloseHours={autoCloseCfg?.[`${subtype}_hours`] || null}
                  user={user}
                  canAdmin={canAdmin}
                  aviScope={aviScope}
                  onEdit={setEditTarget}
                  onDelete={handleDelete}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {view === "list" && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 text-xs">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 rounded-md" style={{ border: "1px solid #2A2A2A", opacity: page <= 1 ? 0.4 : 1 }}
            data-testid="avi-page-prev">Précédent</button>
          <span className="text-muted-foreground">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded-md" style={{ border: "1px solid #2A2A2A", opacity: page >= totalPages ? 0.4 : 1 }}
            data-testid="avi-page-next">Suivant</button>
        </div>
      )}

      {/* Dialog de création */}
      {createOpen && (
        <AviCreateDialog
          subtype={subtype}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}

      {/* Édition rapide depuis la ligne */}
      {editTarget && (
        <AviQuickEditModal
          avi={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {/* Spec rev159 (2026-02-07) — Corbeille AVI (admin only). */}
      <AviTrashModal
        open={trashOpen}
        onOpenChange={setTrashOpen}
        aviType={subtype}
        onChanged={() => load()}
      />

      {/* Spec iter139 pt.2 (Feb 2026) — modale de suppression avec motif obligatoire. */}
      <DeleteReasonModal
        open={!!deleteTarget}
        busy={deleteBusy}
        entityLabel="cette demande"
        entityRef={deleteTarget?.reference || ""}
        onClose={() => { if (!deleteBusy) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
      />

      {/* Spec iter142 Phase 2 (Feb 2026) — modale Fiche AVI (overlay).
          S'ouvre dès que l'URL contient un id (`/avi/:id`), se ferme en
          revenant à `/avi` (préserve les filtres liste via snapshot). */}
      {detailId && (
        <AviDetailModal
          aviId={detailId}
          onClose={() => navigate("/avi")}
        />
      )}
    </div>
  );
}
